import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

export const Privacy: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Header */}
      <header className="header">
        <nav className="nav-container">
          <a href="/">
            <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" />
          </a>
          
          <ul className="nav-menu">
            <li><a href="/#features">Features</a></li>
            <li><a href="/#pricing">Pricing</a></li>
            <li><a href="/#industries">Industries</a></li>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/blog">Blog</a></li>
            <li>
              <button onClick={() => navigate('/login')} className="btn btn-primary">
                Try Gerbo Live
              </button>
            </li>
          </ul>
        </nav>
      </header>

      {/* Legal Content */}
      <div className="legal-container">
        <div className="legal-hero">
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last Updated: February 14, 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section intro">
            <p className="intro-text">
              At Gerbo, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI receptionist service. Please read this policy carefully to understand our views and practices regarding your personal data.
            </p>
            <p className="intro-text">
              By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with this policy, please do not use our Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Information You Provide Directly</h3>
            <p>When you register for and use our Service, we collect:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, business name, business address</li>
              <li><strong>Payment Information:</strong> Credit card details, billing address (processed securely through our payment processors)</li>
              <li><strong>Business Information:</strong> Industry type, business hours, call handling preferences, custom scripts, frequently asked questions</li>
              <li><strong>Communication:</strong> Messages you send to our support team, feedback, survey responses</li>
            </ul>

            <h3>1.2 Information Collected Automatically</h3>
            <p>When you use our Service, we automatically collect:</p>
            <ul>
              <li><strong>Call Data:</strong> Phone numbers (caller and recipient), call duration, time and date of calls, call recordings, call transcripts</li>
              <li><strong>Usage Data:</strong> Features used, frequency of use, time spent in the application, interaction patterns</li>
              <li><strong>Technical Data:</strong> IP address, browser type and version, device type, operating system, unique device identifiers</li>
              <li><strong>Log Data:</strong> Server logs, error reports, performance data</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <p>We may receive information from:</p>
            <ul>
              <li><strong>Telephone Carriers:</strong> Call routing information, caller ID data</li>
              <li><strong>Payment Processors:</strong> Payment confirmation, transaction details</li>
              <li><strong>Analytics Providers:</strong> Usage statistics, demographic information</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information for various purposes:</p>
            
            <h3>2.1 Service Delivery</h3>
            <ul>
              <li>Answering and routing phone calls on your behalf</li>
              <li>Capturing and storing caller information</li>
              <li>Generating and sending call summaries via email or SMS</li>
              <li>Customizing AI responses based on your business information</li>
              <li>Processing payments and managing subscriptions</li>
            </ul>

            <h3>2.2 Service Improvement</h3>
            <ul>
              <li>Analyzing usage patterns to improve AI performance</li>
              <li>Training our AI models for better call handling</li>
              <li>Developing new features and functionality</li>
              <li>Conducting research and analytics</li>
              <li>Testing and troubleshooting issues</li>
            </ul>

            <h3>2.3 Communication</h3>
            <ul>
              <li>Sending service notifications and updates</li>
              <li>Providing customer support</li>
              <li>Sending marketing communications (with your consent)</li>
              <li>Requesting feedback and reviews</li>
              <li>Notifying you of changes to our policies or Service</li>
            </ul>

            <h3>2.4 Legal and Security</h3>
            <ul>
              <li>Complying with legal obligations</li>
              <li>Protecting against fraud and abuse</li>
              <li>Enforcing our Terms of Service</li>
              <li>Resolving disputes</li>
              <li>Ensuring security of our systems and data</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Call Recording and Consent</h2>
            
            <h3>3.1 Recording Practices</h3>
            <p>
              All calls handled by Gerbo are recorded and transcribed. This recording is essential for:
            </p>
            <ul>
              <li>Generating accurate call summaries</li>
              <li>Quality assurance and service improvement</li>
              <li>Training AI models</li>
              <li>Resolving disputes</li>
              <li>Compliance with legal requirements</li>
            </ul>

            <h3>3.2 Your Responsibilities</h3>
            <p>
              By using our Service, you agree to:
            </p>
            <ul>
              <li>Inform your callers that calls may be recorded (where required by law)</li>
              <li>Comply with all applicable call recording laws in your jurisdiction</li>
              <li>Obtain necessary consents from callers where required</li>
            </ul>

            <h3>3.3 Caller Privacy</h3>
            <p>
              Information collected from callers (names, phone numbers, messages) is treated as your data. We process this data on your behalf as a data processor, and you remain the data controller for caller information.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Data Sharing and Disclosure</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>4.1 Service Providers</h3>
            <p>We share data with third-party service providers who perform services on our behalf:</p>
            <ul>
              <li><strong>Cloud Hosting:</strong> AWS, Google Cloud Platform (data storage and processing)</li>
              <li><strong>Payment Processing:</strong> Stripe, PayPal (payment transactions)</li>
              <li><strong>Communication:</strong> SendGrid, Twilio (email and SMS delivery)</li>
              <li><strong>Analytics:</strong> Google Analytics, Mixpanel (usage analytics)</li>
              <li><strong>AI Services:</strong> OpenAI, Anthropic (AI processing)</li>
            </ul>
            <p>These providers are contractually obligated to protect your data and use it only for specified purposes.</p>

            <h3>4.2 Business Transfers</h3>
            <p>
              If Gerbo is involved in a merger, acquisition, or sale of assets, your information may be transferred. We will notify you before your information becomes subject to a different privacy policy.
            </p>

            <h3>4.3 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to:</p>
            <ul>
              <li>Valid legal requests from law enforcement or government authorities</li>
              <li>Court orders or subpoenas</li>
              <li>Protection of our rights, property, or safety</li>
              <li>Protection of the rights, property, or safety of our users or the public</li>
            </ul>

            <h3>4.4 With Your Consent</h3>
            <p>
              We may share your information for any other purpose with your explicit consent.
            </p>

            <h3>4.5 Aggregated Data</h3>
            <p>
              We may share aggregated or anonymized data that cannot identify you individually for research, marketing, or other purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Security</h2>
            
            <h3>5.1 Security Measures</h3>
            <p>We implement industry-standard security measures to protect your data:</p>
            <ul>
              <li><strong>Encryption:</strong> Data is encrypted in transit (TLS/SSL) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring and intrusion detection</li>
              <li><strong>Regular Audits:</strong> Security audits and penetration testing</li>
              <li><strong>Staff Training:</strong> Regular security awareness training for employees</li>
              <li><strong>Incident Response:</strong> Documented procedures for security incidents</li>
            </ul>

            <h3>5.2 Data Breach Notification</h3>
            <p>
              In the event of a data breach affecting your personal information, we will notify you and relevant authorities within 72 hours as required by GDPR and applicable data protection laws.
            </p>

            <h3>5.3 Your Responsibility</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials. Please notify us immediately of any unauthorized access to your account.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            
            <h3>6.1 Retention Periods</h3>
            <p>We retain different types of data for varying periods:</p>
            <ul>
              <li><strong>Call Recordings and Transcripts:</strong> Minimum 90 days, up to 2 years</li>
              <li><strong>Account Information:</strong> Duration of your subscription plus 6 years for legal/tax purposes</li>
              <li><strong>Payment Information:</strong> As required by payment processors and tax law</li>
              <li><strong>Usage Data:</strong> Up to 2 years for analytics and improvement</li>
              <li><strong>Support Communications:</strong> Up to 3 years</li>
            </ul>

            <h3>6.2 Deletion</h3>
            <p>
              After retention periods expire, we securely delete or anonymize your data. You may request earlier deletion of certain data (see Your Rights section).
            </p>

            <h3>6.3 Legal Holds</h3>
            <p>
              We may retain data longer if required by law or if necessary for legal proceedings.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Your Privacy Rights</h2>
            
            <h3>7.1 GDPR Rights (European Users)</h3>
            <p>If you are in the European Economic Area, you have the following rights:</p>
            <ul>
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Restriction:</strong> Limit how we use your data</li>
              <li><strong>Right to Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for data processing</li>
              <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your data protection authority</li>
            </ul>

            <h3>7.2 POPIA Rights (South African Users)</h3>
            <p>Under the Protection of Personal Information Act, you have similar rights including:</p>
            <ul>
              <li>Right to be notified about data collection and use</li>
              <li>Right to access your personal information</li>
              <li>Right to correct or delete personal information</li>
              <li>Right to object to processing</li>
              <li>Right to lodge a complaint with the Information Regulator</li>
            </ul>

            <h3>7.3 Marketing Communications</h3>
            <p>
              You can opt out of marketing emails by clicking the "unsubscribe" link or contacting us. You will still receive service-related communications.
            </p>

            <h3>7.4 Exercising Your Rights</h3>
            <p>
              To exercise any of these rights, contact us at <a href="mailto:privacy@gerboai.com">privacy@gerboai.com</a>. We will respond within 30 days.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. International Data Transfers</h2>
            
            <h3>8.1 Data Location</h3>
            <p>
              Your data is primarily stored and processed in South Africa and the European Union. However, it may be transferred to and processed in other countries where our service providers operate.
            </p>

            <h3>8.2 Safeguards</h3>
            <p>
              When transferring data internationally, we ensure appropriate safeguards are in place:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Adequacy decisions by the European Commission</li>
              <li>Privacy Shield certification (where applicable)</li>
              <li>Other legally approved transfer mechanisms</li>
            </ul>

            <h3>8.3 EU-US Data Transfers</h3>
            <p>
              We comply with the EU-US Data Privacy Framework for data transfers between the EU and United States.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies and Tracking Technologies</h2>
            
            <h3>9.1 Types of Cookies</h3>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security)</li>
              <li><strong>Performance Cookies:</strong> Help us understand how you use the Service</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Marketing Cookies:</strong> Deliver relevant advertisements (with your consent)</li>
            </ul>

            <h3>9.2 Third-Party Cookies</h3>
            <p>We use third-party services that may set cookies:</p>
            <ul>
              <li>Google Analytics (analytics)</li>
              <li>Facebook Pixel (advertising)</li>
              <li>LinkedIn Insight Tag (advertising)</li>
              <li>Hotjar (user experience analysis)</li>
            </ul>

            <h3>9.3 Cookie Management</h3>
            <p>
              You can control cookies through your browser settings. Note that disabling certain cookies may limit functionality of the Service.
            </p>

            <h3>9.4 Do Not Track</h3>
            <p>
              We do not currently respond to Do Not Track signals, as there is no industry standard for how to respond.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately, and we will delete it.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Third-Party Links</h2>
            <p>
              Our Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. AI and Machine Learning</h2>
            
            <h3>12.1 AI Training</h3>
            <p>
              We use call recordings and transcripts to train and improve our AI models. This data is used in aggregate and does not identify individual callers in our training datasets.
            </p>

            <h3>12.2 AI Processing</h3>
            <p>
              Our AI processes call audio and text in real-time to understand caller intent and generate responses. This processing occurs on secure servers and is subject to the same security measures as other data.
            </p>

            <h3>12.3 Human Review</h3>
            <p>
              Selected calls may be reviewed by human quality assurance personnel to improve AI performance. These reviewers are bound by strict confidentiality agreements.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Business Customers and Data Processing</h2>
            
            <h3>13.1 Data Controller vs Data Processor</h3>
            <p>
              For your business account information, Gerbo is the data controller. For information about your callers, you are the data controller and Gerbo is the data processor acting on your instructions.
            </p>

            <h3>13.2 Your Obligations</h3>
            <p>
              As a data controller for caller information, you are responsible for:
            </p>
            <ul>
              <li>Having a legal basis to process caller personal data</li>
              <li>Providing appropriate privacy notices to callers</li>
              <li>Obtaining necessary consents</li>
              <li>Responding to data subject requests from your callers</li>
              <li>Complying with data protection laws in your jurisdiction</li>
            </ul>

            <h3>13.3 Data Processing Agreement</h3>
            <p>
              Our Terms of Service include data processing terms that govern our role as a data processor for caller information.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by:
            </p>
            <ul>
              <li>Email notification to your registered email address</li>
              <li>Prominent notice on our website</li>
              <li>In-app notification when you log in</li>
            </ul>
            <p>
              The updated policy will be effective 30 days after notification unless you object, in which case you may terminate your subscription.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Contact Information</h2>
            <p>
              For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact:
            </p>
            <div className="contact-info">
              <p><strong>Data Protection Officer</strong></p>
              <p>Gerbo AI</p>
              <p>Email: <a href="mailto:privacy@gerboai.com">privacy@gerboai.com</a></p>
              <p>Email: <a href="mailto:dpo@gerboai.com">dpo@gerboai.com</a></p>
              <p>Phone: <a href="tel:+27684887039">+27 68 488 7039</a></p>
            </div>
            
            <h3>15.1 EU Representative</h3>
            <p>
              For users in the European Union, you may contact our EU representative at:
            </p>
            <div className="contact-info">
              <p>Email: <a href="mailto:eu-rep@gerboai.com">eu-rep@gerboai.com</a></p>
            </div>

            <h3>15.2 Supervisory Authority</h3>
            <p>
              If you are in the EU, you have the right to lodge a complaint with your local data protection supervisory authority.
            </p>
            <p>
              If you are in South Africa, you may contact the Information Regulator at <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer">www.justice.gov.za/inforeg</a>
            </p>
          </section>

          <section className="legal-section acknowledgment">
            <h2>Your Consent</h2>
            <p>
              By using our Service, you acknowledge that you have read and understood this Privacy Policy and agree to its terms. If you do not agree with this policy, please do not use our Service.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-column">
              <img src="/images/8EYtClIJaotk40RaLdi5bBD444-1.png" alt="Gerbo Logo" className="logo" style={{ marginBottom: '1.5rem' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', lineHeight: 1.7 }}>Your friendly AI receptionist, always available to help your business grow.</p>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <ul className="footer-links">
                <li><a href="/#features">Features</a></li>
                <li><a href="/#pricing">Pricing</a></li>
                <li><a href="/#industries">Industries</a></li>
                <li><a href="/blog">Blog</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="/contact">Contact</a></li>
                <li><a href="/privacy">Privacy</a></li>
                <li><a href="/terms">Terms</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Support</h4>
              <ul className="footer-links">
                <li><a href="mailto:support@gerboai.com">support@gerboai.com</a></li>
                
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2025 Gerbo. All rights reserved.</p>
            <p>Made by Solid5</p>
          </div>
        </div>
      </footer>
    </>
  );
};